<template>
	<button
		v-on="{ ...$listeners, click: onClick }"
		:id="uid"
		:class="[
			'btn-icon',
			...variantClassList,
			disabledClass(),
			selected && 'is-selected',
			isLoading && 'is-loading',
			hasLoaded && 'has-loaded'
		]"
		:disabled="disabled"
		:type="type"
		:aria-label="ariaLabel"
		:title="title"
	>
		<div class="btn-icon__wrapper">
			<span v-if="variant === 'rotate'" class="btn-icon__icon">
				<i :class="['icon', iconClassName]"></i>
			</span>

			<i v-else :class="['icon', iconClassName]"></i>
			<Badge
				:class="[
					'btn-icon__badge',
					badgeLeading && 'btn-icon__badge--leading',
					badgeTrailing && 'btn-icon__badge--trailing',
					badgeTopLeft && 'btn-icon__badge--top-left',
					badgeTopRight && 'btn-icon__badge--top-right'
				]"
				v-if="showBadge && variant !== 'rotate'"
				:text="badgeContent"
				:visible="showBadge"
			/>
		</div>
		<ButtonProgress
			:isLoading="isLoading"
			:hasLoaded="hasLoaded"
			:hasError="hasError"
			:errorMessage="errorMessage"
			:isOutline="true"
		/>
	</button>
</template>

<script>
import baseComponentMixin from '@/mixins/base-component-mixin';
import baseButtonMixin from '@/mixins/base-button-mixin.js';
import componentVariantMixin from '@/mixins/component-variant-mixin';
import variantClasses from './variant-classes/button-icon-variant-classes';
import ButtonProgress from '@/components/visual/loader/button-progress.vue';
import Badge from '@/components/shells/badge/badge.vue';

/**
 * ButtonIcon
 * @param {('outline'|'topLeft'|'topRight'|'bottomRight'|'bottomRight'|'top'|'bottom'|'left'|'right')} variant - the button's visual variation
 */

export default {
	name: 'ButtonIcon',
	mixins: [baseComponentMixin, baseButtonMixin, componentVariantMixin],
	created() {
		this.variantClasses = variantClasses;
	},
	components: {
		ButtonProgress,
		Badge
	},
	methods: {
		disabledClass() {
			if (this.disabled || this.isLoading || this.hasLoaded)
				return 'is-disabled';

			return '';
		}
	}
};
</script>

<style lang="scss" scoped>
/**
	* This control is also used within the following library controls
	* If you make changes to the HTML architecutre or CSS you need to
	* check the impact on these.
	*
	* Carousel
	* Flyout
	* Modal
	* Popover
	***The following controls have styling exceptions:
	* Search
	* Datepicker
	* Dropdown
	* Dropdown Combo
	* Dropdown Groups
	* Dropdown MultiSelect
	* Conditional Field
	* Input
	*
 */
.btn-icon {
	position: relative;
	@include btn-icon-clear;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	background: none;
	color: var(--bg-0-fg-4);
	border-radius: $border-radius;
	padding: $padding-sm;

	&:hover,
	&:focus,
	&:active {
		color: var(--brand-2);

		.icon:after {
			position: absolute;
			content: '';
			bottom: -$gutter-sm;
			left: 0;
			right: 0;
			border-bottom: 1px solid var(--brand-2);
		}
	}

	&.is-selected {
		background: var(--brand-2-tint);
		color: var(--brand-2);
	}

	.icon {
		position: relative;
	}

	&__wrapper {
		position: relative;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex-grow: 1;
	}

	&__badge {
		@include transform-vertical-center;
	}

	&__badge--leading {
		left: 20%;
		transform: translateX(-100%) translateY(-50%);
	}

	&__badge--trailing {
		right: 20%;
		transform: translateX(100%) translateY(-50%);
	}

	&__badge--top-left {
		left: 20%;
		top: 8%;
		transform: translateX(-100%) translateY(-50%);
	}

	&__badge--top-right {
		right: 20%;
		top: 8%;
		transform: translateX(100%) translateY(-50%);
	}
}

/*------------------------------------*\
  #OUTLINE-VARIANT
\*------------------------------------*/
.btn-icon--outline {
	@include medium-shadow;
	border-radius: 50%;
	border: $btn-border;
	background-color: var(--bg-0);
	color: var(--bg-0-fg-4);
	height: 34px;
	width: 34px;
	min-width: auto; //to create the circle - exception to our button min width

	&:hover,
	&:focus,
	&:active {
		background-color: var(--bg-0);
		color: var(--bg-0-fg-4);
		outline: 0px;

		&:after {
			position: absolute;
			@include transform-full-center;
			content: '';
			height: 34px;
			width: 34px;
			border: $focus-width $focus-style var(--brand-2);
			border-radius: 50%;
		}

		.icon:after {
			border-bottom: transparent;
		}
	}
}

/*------------------------------------*\
  #TOP-LEFT-VARIANT
\*------------------------------------*/
.btn-icon--top-left {
	padding: 0 $padding-md $padding-md 0;
	justify-content: start;
}

/*------------------------------------*\
  #TOP-RIGHT-VARIANT
\*------------------------------------*/
.btn-icon--top-right {
	padding: 0 0 $padding-md $padding-md;
	justify-content: end;
}

/*------------------------------------*\
  #BOTTOM-LEFT-VARIANT
\*------------------------------------*/
.btn-icon--bottom-left {
	padding: $padding-md $padding-md 0 0;
	justify-content: start;
}

/*------------------------------------*\
  #BOTTOM-RIGHT-VARIANT
\*------------------------------------*/
.btn-icon--bottom-right {
	padding: $padding-md 0 0 $padding-md;
	justify-content: end;
}

/*------------------------------------*\
  #TOP-VARIANT
\*------------------------------------*/
.btn-icon--top {
	padding: 0 $padding-sm $padding-sm $padding-sm;
	align-items: flex-start;
}

/*------------------------------------*\
  #BOTTOM-VARIANT
\*------------------------------------*/
.btn-icon--bottom {
	padding: $padding-sm $padding-sm 0 $padding-sm;
	align-items: flex-end;
}

/*------------------------------------*\
  #LEFT-VARIANT
\*------------------------------------*/
.btn-icon--left {
	padding: $padding-sm $padding-sm $padding-sm 0;
	justify-content: start;
}

/*------------------------------------*\
  #RIGHT-VARIANT
\*------------------------------------*/
.btn-icon--right {
	padding: $padding-sm 0 $padding-sm $padding-sm;
	justify-content: end;
}

/*------------------------------------*\
  #ROTATE-VARIANT
\*------------------------------------*/
.btn-icon--rotate {
	&:hover,
	&:focus,
	&:active {
		.icon:after {
			content: none;
		}

		.btn-icon__icon:after {
			position: absolute;
			content: '';
			bottom: -$gutter-xs;
			left: 0;
			right: 0;
			border-bottom: 1px solid var(--brand-2);
		}
	}

	.icon {
		position: relative;
		display: inline-block;
	}
}

/* Tweak for Size in Tables and Cards */
.table {
	& .btn-icon {
		font-size: $table-icon-font-size;
		padding-top: 0;
		padding-bottom: 0;
	}
}
.card {
	& .btn-icon {
		font-size: $card-icon-font-size;
	}
}
</style>
